import { LayoutDashboard } from 'lucide-react'
import { useEffect, useState, useCallback, useRef } from 'react'

import { DashboardBoard } from '@/components/dashboard/DashboardBoard'
import { Switch } from '@/components/ui/switch'
import { useDashboard } from '@/hooks/useDashboard'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { DashboardAnalyticsResponse, DashboardWidget } from '@/types/api/dashboard'
import { logger } from '@/utils/logger'

interface RecordGroupDashboardProps {
  recordGroupId: string
}

/**
 * The record group's own dashboard. Its widgets only ever read this group (and, when switched on,
 * the sub-groups the viewer may see): the server forces that scope for every query that carries
 * the dashboard id, so nothing from other groups can appear here.
 */
export function RecordGroupDashboard({ recordGroupId }: RecordGroupDashboardProps) {
  const {
    dashboard, updateDashboard, queryAnalytics,
    createWidget, deleteWidget, resizeWidget,
    isSaving, isLoading, error,
  } = useDashboard(undefined, { recordGroupId })

  const [widgets, setWidgets] = useState<DashboardWidget[]>([])
  const [widgetData, setWidgetData] = useState<Record<string, DashboardAnalyticsResponse | undefined>>({})
  const widgetCursors = useRef<Record<string, { lastCreatedAt: string; lastId: string } | null>>({})
  const dashboardId = dashboard?.id ?? undefined
  // A viewer of a group without a dashboard yet gets an unsaved (id-less), read-only one.
  const canEdit = !!dashboardId && (dashboard?.allowedActions ?? []).includes(PERMISSION_KEYS.DASHBOARD_UPDATE)
  const includeSubgroups = !!dashboard?.includeSubgroups

  // Scope changes (sub-groups on/off) invalidate every widget's data
  useEffect(() => {
    setWidgetData({})
    widgetCursors.current = {}
  }, [includeSubgroups])

  useEffect(() => {
    if (dashboard?.widgets) {
      setWidgets(dashboard.widgets)
    }
  }, [dashboard])

  useEffect(() => {
    let cancelled = false
    async function loadWidgetData() {
      const entries = await Promise.all(
        widgets.map(async (widget) => {
          try {
            const data = await queryAnalytics({
              dataSource: widget.dataSource,
              config: widget.dataSourceConfig ?? {},
              dashboardId,
            })
            if (data?.lastCreatedAt && data?.lastId) {
              widgetCursors.current[widget.id] = { lastCreatedAt: data.lastCreatedAt, lastId: data.lastId }
            }
            return [widget.id, data] as const
          } catch {
            logger.warn('Failed to load widget data', widget.dataSource)
            return [widget.id, { message: 'Failed to load widget data' } as DashboardAnalyticsResponse] as const
          }
        })
      )
      if (!cancelled) setWidgetData(Object.fromEntries(entries))
    }
    if (dashboardId && widgets.length > 0 && widgets.some(w => !widgetData[w.id])) {
      void loadWidgetData()
    }
    return () => { cancelled = true }
  }, [widgets, queryAnalytics, dashboardId, widgetData])

  const handleLoadMore = useCallback(async (widgetId: string, dataSource: string, config: Record<string, unknown>) => {
    const cursor = widgetCursors.current[widgetId]
    const page = await queryAnalytics({
      dataSource,
      dashboardId,
      config: {
        ...config,
        ...(cursor ? { cursorCreatedAt: cursor.lastCreatedAt, cursorId: cursor.lastId } : {}),
        size: 20,
      },
    })
    setWidgetData((prev) => {
      const existing = prev[widgetId]
      if (!existing) return prev
      return { ...prev, [widgetId]: { ...page, items: [...(existing.items ?? []), ...(page.items ?? [])] } }
    })
    if (page.lastCreatedAt && page.lastId) {
      widgetCursors.current[widgetId] = { lastCreatedAt: page.lastCreatedAt, lastId: page.lastId }
    }
  }, [queryAnalytics, dashboardId])

  const persistSettings = (overrides: Partial<{
    title: string
    description: string | null
  }>) => {
    if (!dashboard) return
    if (!dashboard.id) return
    updateDashboard({
      dashboardId: dashboard.id,
      data: {
        title: overrides?.title ?? dashboard.title,
        description: overrides?.description ?? dashboard.description,
      },
    })
  }

  const handleCreateWidget = useCallback(async (widgetDataToCreate: Omit<DashboardWidget, 'id' | 'createdAt' | 'updatedAt' | 'dashboardId'> & { tempId?: string }) => {
    if (!dashboard?.id) return
    const { tempId, ...rest } = widgetDataToCreate
    try {
      const created = await createWidget({
        dashboardId: dashboard.id,
        ...rest
      })
      setWidgets((prev) => {
        if (tempId && prev.some(w => w.id === tempId)) {
          return prev.map(w => w.id === tempId ? { ...created } : w)
        }
        return [...prev, created]
      })
    } catch (err) {
      logger.error('Failed to create widget', err, 'expected')
    }
  }, [dashboard?.id, createWidget])

  const handleDeleteWidget = useCallback(async (widgetId: string) => {
    if (!dashboard?.id) return
    if (widgetId.startsWith('temp-')) {
      setWidgets((prev) => prev.filter((w) => w.id !== widgetId))
      return
    }
    try {
      await deleteWidget({ dashboardId: dashboard.id, widgetId })
    } catch (err) {
      logger.error('Failed to delete widget', err, 'expected')
    }
  }, [dashboard?.id, deleteWidget])

  const handleResizeWidget = useCallback(async (widgetId: string, w: number, h: number) => {
    if (!dashboard?.id || widgetId.startsWith('temp-')) return
    try {
      await resizeWidget({ dashboardId: dashboard.id, widgetId, w, h })
    } catch (err) { logger.error('Failed to resize widget', err, 'expected') }
  }, [dashboard?.id, resizeWidget])

  if (error) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-10 text-center text-neutral-500">
        You don't have permission to view this record group's dashboard.
      </div>
    )
  }

  return (
    <DashboardBoard
      headerTitle={
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="flex items-center gap-2 text-xl font-bold">
            <LayoutDashboard className="h-5 w-5" />
            Record Group Dashboard
          </h2>
          {canEdit ? (
            <label className="flex cursor-pointer items-center gap-2 text-sm font-normal text-muted-foreground">
              <Switch
                checked={includeSubgroups}
                onCheckedChange={(next) => dashboard?.id && updateDashboard({
                  dashboardId: dashboard.id,
                  data: { includeSubgroups: next },
                })}
                aria-label="Include sub-groups in this dashboard"
              />
              Include sub-groups
            </label>
          ) : includeSubgroups && (
            <span className="text-sm font-normal text-muted-foreground">Includes sub-groups</span>
          )}
        </div>
      }
      headerDescription="Widgets here only show this record group's records and activity."
      dashboard={dashboard}
      canEdit={canEdit}
      onLoadMore={handleLoadMore}
      widgets={widgets}
      widgetData={widgetData}
      isLoading={isLoading}
      isSaving={isSaving}
      lockedRecordGroupId={recordGroupId}
      onCreateWidget={handleCreateWidget}
      onDeleteWidget={handleDeleteWidget}
      onResizeWidget={handleResizeWidget}
      onWidgetsChange={(newWidgets: DashboardWidget[]) => {
        // Find if any widget's dataSource or dataSourceConfig changed and clear stale data
        const changedIds = newWidgets.filter((w, i) => {
          const old = widgets[i]
          if (!old || old.id !== w.id) return false
          return old.dataSource !== w.dataSource || 
            JSON.stringify(old.dataSourceConfig) !== JSON.stringify(w.dataSourceConfig)
        }).map(w => w.id)
        if (changedIds.length > 0) {
          setWidgetData(prev => {
            const next = { ...prev }
            changedIds.forEach(id => delete next[id])
            return next
          })
        }
        setWidgets(newWidgets)
      }}
      onSaveSettings={(updates) =>
        persistSettings({
          title: updates.title,
          description: updates.description,
        })
      }
    />
  )
}
